import { useCallback, useEffect, useMemo, useState } from "react";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type {
  MarkElementTypesReviewed,
  MarkRouteTypesReviewed,
} from "../types";
import type { HistoryRowChange } from "../HistoryDrawer/types";

export interface ApprovalStatusStateV3 {
  approvedElementTypeIds: Set<number>;
  approvedRouteTypeIds: Set<number>;
  approvedDiagramItemTypeIds: Set<number>;
}

export interface ApprovalStatusLoadingV3 {
  isLoadingApprovals: boolean;
}

export interface ApplyApprovalChangesArgs {
  changes: HistoryRowChange[];
}

export interface ApprovalStatusActionsV3 {
  // Mirrors the approval rows a revert created or deleted; no refetch needed.
  applyApprovalChanges: (args: ApplyApprovalChangesArgs) => void;
  toggleFloorPlanGroupApproval: (elementTypeId: number) => void;
  toggleCableGroupApproval: (routeTypeId: number) => void;
  toggleQuantityGroupApproval: (diagramItemTypeId: number) => void;
  bulkApproveElementTypes: (
    elementTypeIds: number[],
    approved: boolean,
  ) => void;
  bulkApproveRouteTypes: (routeTypeIds: number[], approved: boolean) => void;
  bulkApproveQuantityTypes: (
    diagramItemTypeIds: number[],
    approved: boolean,
  ) => void;
  isFloorPlanGroupApproved: (elementTypeId: number) => boolean;
  isCableGroupApproved: (routeTypeId: number) => boolean;
  isQuantityGroupApproved: (diagramItemTypeId: number) => boolean;
  allPlanElementsApproved: boolean;
  allPlanCablesApproved: boolean;
  allDiagramItemsApproved: boolean;
}

export interface UseApprovalStatusV3Return
  extends
    ApprovalStatusStateV3,
    ApprovalStatusLoadingV3,
    ApprovalStatusActionsV3 {}

interface ToggleIdInSetArgs {
  set: Set<number>;
  id: number;
}

type ToggleIdInSet = (args: ToggleIdInSetArgs) => Set<number>;

const toggleIdInSet: ToggleIdInSet = ({ set, id }) => {
  const next = new Set(set);
  next.has(id) ? next.delete(id) : next.add(id);
  return next;
};

// An approval row fills one of its three link columns; a null one leaves its set untouched.
const ensureApproval =
  (id: number | null, approved: boolean) =>
  (approvedIds: Set<number>): Set<number> =>
    id === null || approvedIds.has(id) === approved
      ? approvedIds
      : toggleIdInSet({ set: approvedIds, id });

export interface UseApprovalStatusV3Args {
  projectId: string | number;
  planElementTypeIds: number[];
  planRouteTypeIds: number[];
  selectedDiagramItemTypeIds: number[];
  /** Skipped when no plan is selected. */
  floorPlanId: number | undefined;
  /** On approve, promote name_status to confident in the local plan cache. */
  markElementTypesReviewed: MarkElementTypesReviewed;
  markRouteTypesReviewed: MarkRouteTypesReviewed;
  /** Read-only users keep the visible state; every mutation is a no-op. */
  canEdit: boolean;
  stopTraversing: () => void;
  // Called after every approval write the server accepted; the history list is stale from then on.
  onApprovalsChanged: () => void;
}

export const useApprovalStatusV3 = ({
  projectId,
  planElementTypeIds,
  planRouteTypeIds,
  selectedDiagramItemTypeIds,
  floorPlanId,
  markElementTypesReviewed,
  markRouteTypesReviewed,
  canEdit,
  stopTraversing,
  onApprovalsChanged,
}: UseApprovalStatusV3Args): UseApprovalStatusV3Return => {
  const [isLoadingApprovals, setIsLoadingApprovals] = useState(true);

  const [approvedElementTypeIds, setApprovedElementTypeIds] = useState<
    Set<number>
  >(new Set());
  const [approvedRouteTypeIds, setApprovedRouteTypeIds] = useState<Set<number>>(
    new Set(),
  );
  const [approvedDiagramItemTypeIds, setApprovedDiagramItemTypeIds] = useState<
    Set<number>
  >(new Set());

  useEffect(() => {
    const fetchApprovals = async () => {
      try {
        const approvals: Array<{
          elementTypeId: number | null;
          routeTypeId: number | null;
          diagramItemTypeId: number | null;
        }> = await api.getQuantitySurveyorApprovals(projectId);

        const elementTypes = new Set<number>();
        const routeTypes = new Set<number>();
        const diagramItemTypes = new Set<number>();

        for (const a of approvals) {
          if (a.elementTypeId !== null && a.elementTypeId !== undefined)
            elementTypes.add(a.elementTypeId);
          if (a.routeTypeId !== null && a.routeTypeId !== undefined)
            routeTypes.add(a.routeTypeId);
          if (a.diagramItemTypeId !== null && a.diagramItemTypeId !== undefined)
            diagramItemTypes.add(a.diagramItemTypeId);
        }

        setApprovedElementTypeIds(elementTypes);
        setApprovedRouteTypeIds(routeTypes);
        setApprovedDiagramItemTypeIds(diagramItemTypes);
      } catch (err: unknown) {
        console.error("Failed to fetch approvals:", err);
      } finally {
        setIsLoadingApprovals(false);
      }
    };

    fetchApprovals();
  }, [projectId]);

  const numericProjectId = Number(projectId);

  const toggleFloorPlanGroupApproval = useCallback(
    async (elementTypeId: number) => {
      if (!canEdit) return;
      const willApprove = !approvedElementTypeIds.has(elementTypeId);
      setApprovedElementTypeIds((prev) =>
        toggleIdInSet({ set: prev, id: elementTypeId }),
      );

      if (willApprove) {
        stopTraversing();
      }

      try {
        await api.toggleQuantitySurveyorApproval({
          projectId: numericProjectId,
          elementTypeId,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_APPROVAL_TOGGLED, {
          groupKind: "elements",
          groupTypeId: String(elementTypeId),
          approved: willApprove,
        });
        if (willApprove && floorPlanId !== undefined) {
          markElementTypesReviewed({
            floorPlanId,
            elementTypeIds: [elementTypeId],
          });
        }
        onApprovalsChanged();
      } catch (err: unknown) {
        console.error("Failed to toggle floor plan approval:", err);
        setApprovedElementTypeIds((prev) =>
          toggleIdInSet({ set: prev, id: elementTypeId }),
        );
      }
    },
    [
      canEdit,
      approvedElementTypeIds,
      numericProjectId,
      floorPlanId,
      markElementTypesReviewed,
      stopTraversing,
      onApprovalsChanged,
    ],
  );

  const toggleCableGroupApproval = useCallback(
    async (routeTypeId: number) => {
      if (!canEdit) return;
      const willApprove = !approvedRouteTypeIds.has(routeTypeId);
      setApprovedRouteTypeIds((prev) =>
        toggleIdInSet({ set: prev, id: routeTypeId }),
      );

      if (willApprove) {
        stopTraversing();
      }

      try {
        await api.toggleQuantitySurveyorApproval({
          projectId: numericProjectId,
          routeTypeId,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_APPROVAL_TOGGLED, {
          groupKind: "routes",
          groupTypeId: String(routeTypeId),
          approved: willApprove,
        });
        if (willApprove && floorPlanId !== undefined) {
          markRouteTypesReviewed({
            floorPlanId,
            routeTypeIds: [routeTypeId],
          });
        }
        onApprovalsChanged();
      } catch (err: unknown) {
        console.error("Failed to toggle cable approval:", err);
        setApprovedRouteTypeIds((prev) =>
          toggleIdInSet({ set: prev, id: routeTypeId }),
        );
      }
    },
    [
      canEdit,
      approvedRouteTypeIds,
      numericProjectId,
      floorPlanId,
      markRouteTypesReviewed,
      stopTraversing,
      onApprovalsChanged,
    ],
  );

  const toggleQuantityGroupApproval = useCallback(
    async (diagramItemTypeId: number) => {
      if (!canEdit) return;
      const willApprove = !approvedDiagramItemTypeIds.has(diagramItemTypeId);
      setApprovedDiagramItemTypeIds((prev) =>
        toggleIdInSet({ set: prev, id: diagramItemTypeId }),
      );

      if (willApprove) {
        stopTraversing();
      }

      try {
        await api.toggleQuantitySurveyorApproval({
          projectId: numericProjectId,
          diagramItemTypeId,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_APPROVAL_TOGGLED, {
          groupKind: "quantities",
          groupTypeId: String(diagramItemTypeId),
          approved: willApprove,
        });
        onApprovalsChanged();
      } catch (err: unknown) {
        console.error("Failed to toggle quantity approval:", err);
        setApprovedDiagramItemTypeIds((prev) =>
          toggleIdInSet({ set: prev, id: diagramItemTypeId }),
        );
      }
    },
    [
      canEdit,
      approvedDiagramItemTypeIds,
      numericProjectId,
      stopTraversing,
      onApprovalsChanged,
    ],
  );

  const applyApprovalChanges = useCallback(
    ({ changes }: ApplyApprovalChangesArgs) => {
      for (const change of changes) {
        if (change.table !== "approvals") continue;
        const approval = change.after ?? change.before;
        if (approval === null || change.changeType === "updated") continue;
        const approved = change.changeType === "created";
        setApprovedElementTypeIds(
          ensureApproval(approval.elementTypeId, approved),
        );
        setApprovedRouteTypeIds(ensureApproval(approval.routeTypeId, approved));
        setApprovedDiagramItemTypeIds(
          ensureApproval(approval.diagramItemTypeId, approved),
        );
      }
    },
    [],
  );

  const bulkApproveElementTypes = useCallback(
    async (elementTypeIds: number[], approved: boolean) => {
      if (!canEdit) return;
      let snapshot: Set<number> | null = null;

      setApprovedElementTypeIds((prev) => {
        snapshot = prev;
        const next = new Set(prev);
        for (const id of elementTypeIds) {
          approved ? next.add(id) : next.delete(id);
        }
        return next;
      });

      if (approved) {
        stopTraversing();
      }

      try {
        await api.bulkApproveElementTypes({
          projectId: numericProjectId,
          elementTypeIds,
          approved,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUPS_BULK_APPROVED, {
          groupKind: "elements",
          groupCount: elementTypeIds.length,
          approved,
        });
        if (approved && floorPlanId !== undefined) {
          markElementTypesReviewed({ floorPlanId, elementTypeIds });
        }
        onApprovalsChanged();
      } catch (err: unknown) {
        console.error("Failed to bulk approve element types:", err);
        if (snapshot !== null) {
          setApprovedElementTypeIds(snapshot);
        }
      }
    },
    [
      canEdit,
      numericProjectId,
      floorPlanId,
      markElementTypesReviewed,
      stopTraversing,
      onApprovalsChanged,
    ],
  );

  const bulkApproveRouteTypes = useCallback(
    async (routeTypeIds: number[], approved: boolean) => {
      if (!canEdit) return;
      let snapshot: Set<number> | null = null;

      setApprovedRouteTypeIds((prev) => {
        snapshot = prev;
        const next = new Set(prev);
        for (const id of routeTypeIds) {
          approved ? next.add(id) : next.delete(id);
        }
        return next;
      });

      if (approved) {
        stopTraversing();
      }

      try {
        await api.bulkApproveRouteTypes({
          projectId: numericProjectId,
          routeTypeIds,
          approved,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUPS_BULK_APPROVED, {
          groupKind: "routes",
          groupCount: routeTypeIds.length,
          approved,
        });
        if (approved && floorPlanId !== undefined) {
          markRouteTypesReviewed({ floorPlanId, routeTypeIds });
        }
        onApprovalsChanged();
      } catch (err: unknown) {
        console.error("Failed to bulk approve route types:", err);
        if (snapshot !== null) {
          setApprovedRouteTypeIds(snapshot);
        }
      }
    },
    [
      canEdit,
      numericProjectId,
      floorPlanId,
      markRouteTypesReviewed,
      stopTraversing,
      onApprovalsChanged,
    ],
  );

  const allPlanElementsApproved = useMemo(
    () =>
      planElementTypeIds.length > 0 &&
      planElementTypeIds.every((id) => approvedElementTypeIds.has(id)),
    [planElementTypeIds, approvedElementTypeIds],
  );

  const bulkApproveQuantityTypes = useCallback(
    async (diagramItemTypeIds: number[], approved: boolean) => {
      if (!canEdit) return;
      let snapshot: Set<number> | null = null;

      setApprovedDiagramItemTypeIds((prev) => {
        snapshot = prev;
        const next = new Set(prev);
        for (const id of diagramItemTypeIds) {
          approved ? next.add(id) : next.delete(id);
        }
        return next;
      });

      if (approved) {
        stopTraversing();
      }

      try {
        await api.bulkApproveQuantityTypes({
          projectId: numericProjectId,
          diagramItemTypeIds,
          approved,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUPS_BULK_APPROVED, {
          groupKind: "quantities",
          groupCount: diagramItemTypeIds.length,
          approved,
        });
        onApprovalsChanged();
      } catch (err: unknown) {
        console.error("Failed to bulk approve quantity types:", err);
        if (snapshot !== null) {
          setApprovedDiagramItemTypeIds(snapshot);
        }
      }
    },
    [canEdit, numericProjectId, stopTraversing, onApprovalsChanged],
  );

  const allDiagramItemsApproved = useMemo(
    () =>
      selectedDiagramItemTypeIds.length > 0 &&
      selectedDiagramItemTypeIds.every((id) =>
        approvedDiagramItemTypeIds.has(id),
      ),
    [selectedDiagramItemTypeIds, approvedDiagramItemTypeIds],
  );

  const allPlanCablesApproved = useMemo(
    () =>
      planRouteTypeIds.length > 0 &&
      planRouteTypeIds.every((id) => approvedRouteTypeIds.has(id)),
    [planRouteTypeIds, approvedRouteTypeIds],
  );

  const isFloorPlanGroupApproved = useCallback(
    (elementTypeId: number) => approvedElementTypeIds.has(elementTypeId),
    [approvedElementTypeIds],
  );

  const isCableGroupApproved = useCallback(
    (routeTypeId: number) => approvedRouteTypeIds.has(routeTypeId),
    [approvedRouteTypeIds],
  );

  const isQuantityGroupApproved = useCallback(
    (diagramItemTypeId: number) =>
      approvedDiagramItemTypeIds.has(diagramItemTypeId),
    [approvedDiagramItemTypeIds],
  );

  return {
    applyApprovalChanges,
    isLoadingApprovals,
    approvedElementTypeIds,
    approvedRouteTypeIds,
    approvedDiagramItemTypeIds,
    allPlanElementsApproved,
    allPlanCablesApproved,
    allDiagramItemsApproved,
    toggleFloorPlanGroupApproval,
    toggleCableGroupApproval,
    toggleQuantityGroupApproval,
    bulkApproveElementTypes,
    bulkApproveRouteTypes,
    bulkApproveQuantityTypes,
    isFloorPlanGroupApproved,
    isCableGroupApproved,
    isQuantityGroupApproved,
  };
};
